import { useEffect, useRef, useState } from 'react';
import {
    CpmBadge,
    CpmButton,
    CpmCheckbox,
    CpmFilterDropdown,
} from '../../../components/design-system';
import {
    applyTemplateToStoreBrands,
    getReportsAdminErrorMessage,
    reportsAdminErrorReason,
    getTemplateDetails,
    listStates,
    listStoreBrands,
    listTemplates,
} from '../../../services/reportsAdminService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type {
    ApplyTemplateResult,
    FormPhase,
    PlaceLoadState,
    StoreBrandListRow,
    TemplateDetails,
    TemplateListRow,
} from '../../../types/reportsAdmin.types';
import { REPORTS_COPY } from '../copy';
import { canTurnOn, plural, stateName } from '../helpers';
import {
    ReportsAlert,
    ReportsDialog,
    ReportsDialogBody,
    ReportsDialogFooter,
    ReportsDialogHeader,
    ReportsSkeletonList,
    ReportsWait,
} from '../components/ReportsDialog';
import AppliedFilterChips from '../components/AppliedFilterChips';
import FilteredEmpty from '../components/FilteredEmpty';
import InfiniteScrollSentinel from '../components/InfiniteScrollSentinel';
import SearchField from '../components/SearchField';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

export interface PresetStoreBrand {
    storeBrandId: string;
    name?: string;
    state?: string | null;
}

interface ApplyTemplateModalProps {
    source: 'templates' | 'brands';
    templateId?: string;
    presetStoreBrandIds?: string[];
    presetStoreBrands?: PresetStoreBrand[];
    onClose: () => void;
    onApplied: () => void;
}

export default function ApplyTemplateModal({
    source,
    templateId,
    presetStoreBrandIds,
    presetStoreBrands,
    onClose,
    onApplied,
}: ApplyTemplateModalProps) {
    const fromBrands = source === 'brands';
    const targetIds = (presetStoreBrands ?? presetStoreBrandIds ?? []).map((row) =>
        typeof row === 'string' ? row : row.storeBrandId
    );
    const targetIdsKey = targetIds.join(',');
    const namedTargets: PresetStoreBrand[] = presetStoreBrands?.length
        ? presetStoreBrands
        : targetIds.map((storeBrandId) => ({ storeBrandId }));
    const openedTemplateStates = defaultOpenedTemplateStates(namedTargets);

    const [phase, setPhase] = useState<FormPhase>('idle');
    const [error, setError] = useState('');
    const [receipt, setReceipt] = useState<ApplyTemplateResult | null>(null);
    const [stateDocs, setStateDocs] = useState<CpmState[]>([]);
    const [statesFailed, setStatesFailed] = useState(false);

    const [pack, setPack] = useState<TemplateDetails | null>(null);
    const [packState, setPackState] = useState<PlaceLoadState>(fromBrands ? 'idle' : 'loading');

    const [templates, setTemplates] = useState<TemplateListRow[]>([]);
    const [templateTotal, setTemplateTotal] = useState(0);
    const [templatePage, setTemplatePage] = useState(1);
    const [templateSearch, setTemplateSearch] = useState('');
    const debouncedTemplate = useDebouncedValue(templateSearch, 300);
    const [templateStates, setTemplateStates] = useState(openedTemplateStates);
    const [templatesState, setTemplatesState] = useState<PlaceLoadState>(
        fromBrands ? 'loading' : 'idle'
    );
    const [templatesBusy, setTemplatesBusy] = useState(false);
    const templatesReadyOnce = useRef(false);

    const [brandSearch, setBrandSearch] = useState('');
    const debouncedBrand = useDebouncedValue(brandSearch, 300);
    const [brandStates, setBrandStates] = useState<string[]>([]);
    const [brands, setBrands] = useState<StoreBrandListRow[]>([]);
    const [brandTotal, setBrandTotal] = useState(0);
    const [brandPage, setBrandPage] = useState(1);
    const [add, setAdd] = useState(0);
    const [keep, setKeep] = useState(0);
    const [legalCount, setLegalCount] = useState(0);
    const [universeTotal, setUniverseTotal] = useState(0);
    const [brandsState, setBrandsState] = useState<PlaceLoadState>(fromBrands ? 'idle' : 'loading');
    const [brandsBusy, setBrandsBusy] = useState(false);
    const brandsReadyOnce = useRef(false);
    const [browse, setBrowse] = useState(false);
    const [showSkipped, setShowSkipped] = useState(false);
    const [ticked, setTicked] = useState<string[]>([]);
    const [selectAllMatching, setSelectAllMatching] = useState(false);
    const [excluded, setExcluded] = useState<string[]>([]);
    const [reload, setReload] = useState(0);
    const [templateMoreBusy, setTemplateMoreBusy] = useState(false);
    const [templateMoreFailed, setTemplateMoreFailed] = useState(false);
    const [brandMoreBusy, setBrandMoreBusy] = useState(false);
    const [brandMoreFailed, setBrandMoreFailed] = useState(false);

    const locked = phase === 'saving';
    const pickedId = fromBrands ? (pack?._id ?? '') : (templateId ?? '');
    const brandStatesKey = brandStates.join('|');
    const templateStatesKey = templateStates.join('|');
    const oneBrand = namedTargets.length === 1;
    const legalReports = (pack?.reports ?? []).filter(canTurnOn);
    const illegalReports = (pack?.reports ?? []).filter((row) => !canTurnOn(row));
    const blockReasons = packBlockReasons(pack);

    const loadStates = () => {
        setStatesFailed(false);
        void listStates()
            .then(setStateDocs)
            .catch(() => setStatesFailed(true));
    };

    useEffect(() => {
        loadStates();
    }, []);

    useEffect(() => {
        if (fromBrands || !templateId) return;
        let cancelled = false;
        setPackState('loading');
        if (!brandsReadyOnce.current) setBrandsState('loading');
        void getTemplateDetails(templateId)
            .then((details) => {
                if (cancelled) return;
                setPack(details);
                setBrandStates(details.allStates ? [] : details.states);
                setPackState('ready');
            })
            .catch((err) => {
                if (cancelled) return;
                setError(getReportsAdminErrorMessage(err));
                setPackState('error');
                setBrandsState('idle');
            });
        return () => {
            cancelled = true;
        };
    }, [fromBrands, reload, templateId]);

    useEffect(() => {
        if (!fromBrands) return;
        let cancelled = false;
        if (templatesReadyOnce.current) setTemplatesBusy(true);
        else setTemplatesState('loading');
        setTemplateMoreFailed(false);
        void listTemplates({
            activeStatus: 'Active',
            search: debouncedTemplate || undefined,
            states: templateStatesKey ? templateStatesKey.split('|') : undefined,
            page: 1,
            limit: 25,
        })
            .then((data) => {
                if (cancelled) return;
                setTemplates(data.templates);
                setTemplateTotal(data.total);
                setTemplatePage(1);
                templatesReadyOnce.current = true;
                setTemplatesState('ready');
            })
            .catch((err) => {
                if (cancelled) return;
                setError(getReportsAdminErrorMessage(err));
                if (!templatesReadyOnce.current) setTemplatesState('error');
            })
            .finally(() => {
                if (!cancelled) setTemplatesBusy(false);
            });
        return () => {
            cancelled = true;
        };
    }, [fromBrands, debouncedTemplate, reload, templateStatesKey]);

    useEffect(() => {
        if (!pickedId) return;
        if (!fromBrands && packState !== 'ready') return;
        if (fromBrands && !targetIdsKey) return;
        let cancelled = false;
        if (brandsReadyOnce.current) setBrandsBusy(true);
        else setBrandsState('loading');
        setBrandMoreFailed(false);
        void listStoreBrands({
            templateId: pickedId,
            search: fromBrands ? undefined : debouncedBrand || undefined,
            states: fromBrands ? undefined : brandStatesKey ? brandStatesKey.split('|') : undefined,
            storeBrandIds: fromBrands ? targetIdsKey.split(',') : undefined,
            page: 1,
            limit: 25,
        })
            .then((data) => {
                if (cancelled) return;
                setBrands(data.storeBrands);
                setBrandTotal(data.total);
                setAdd(data.add ?? 0);
                setKeep(data.keep ?? 0);
                setLegalCount(data.legalCount ?? 0);
                setBrandPage(1);
                if (!fromBrands && !debouncedBrand && !brandStatesKey) {
                    setUniverseTotal(data.total);
                } else if (fromBrands) {
                    setUniverseTotal(data.total);
                }
                brandsReadyOnce.current = true;
                setBrandsState('ready');
            })
            .catch((err) => {
                if (cancelled) return;
                setError(getReportsAdminErrorMessage(err));
                if (!brandsReadyOnce.current) setBrandsState('error');
            })
            .finally(() => {
                if (!cancelled) setBrandsBusy(false);
            });
        return () => {
            cancelled = true;
        };
    }, [brandStatesKey, debouncedBrand, fromBrands, packState, pickedId, reload, targetIdsKey]);

    const loadPack = async (id: string) => {
        setPackState('loading');
        setError('');
        try {
            setPack(await getTemplateDetails(id));
            setPackState('ready');
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPackState('error');
        }
    };

    const loadTemplatesPage = async (nextPage: number) => {
        setTemplateMoreBusy(true);
        setTemplateMoreFailed(false);
        try {
            const data = await listTemplates({
                activeStatus: 'Active',
                search: debouncedTemplate || undefined,
                states: templateStates.length ? templateStates : undefined,
                page: nextPage,
                limit: 25,
            });
            setTemplates((prev) => [...prev, ...data.templates]);
            setTemplateTotal(data.total);
            setTemplatePage(nextPage);
        } catch {
            setTemplateMoreFailed(true);
        } finally {
            setTemplateMoreBusy(false);
        }
    };

    const loadBrandsPage = async (nextPage: number) => {
        if (!pickedId) return;
        setBrandMoreBusy(true);
        setBrandMoreFailed(false);
        try {
            const data = await listStoreBrands({
                templateId: pickedId,
                search: fromBrands ? undefined : debouncedBrand || undefined,
                states: fromBrands ? undefined : brandStates.length ? brandStates : undefined,
                storeBrandIds: fromBrands ? targetIds : undefined,
                page: nextPage,
                limit: 25,
            });
            setBrands((prev) => [...prev, ...data.storeBrands]);
            setBrandTotal(data.total);
            setBrandPage(nextPage);
        } catch {
            setBrandMoreFailed(true);
        } finally {
            setBrandMoreBusy(false);
        }
    };

    const pickTemplate = (id: string) => {
        if (locked || id === pack?._id) return;
        setReceipt(null);
        setError('');
        void loadPack(id);
    };

    const narrowed = Boolean(debouncedBrand) || brandStates.length > 0;
    const painting = narrowed || browse;
    const paintedRows = showSkipped ? brands : brands.filter((row) => row.wouldAdd);
    const selectedIds = selectAllMatching
        ? paintedRows
              .map((row) => row.storeBrandId)
              .filter((id) => !excluded.includes(id))
              .concat(ticked.filter((id) => !excluded.includes(id)))
        : ticked;

    const selectedPainted = brands.filter((row) =>
        selectAllMatching ? !excluded.includes(row.storeBrandId) : ticked.includes(row.storeBrandId)
    );
    const selectedAdd = selectAllMatching
        ? Math.max(
              add -
                  excluded.filter((id) => brands.find((row) => row.storeBrandId === id)?.wouldAdd)
                      .length,
              0
          )
        : selectedPainted.filter((row) => row.wouldAdd).length;
    const selectedKeep = selectAllMatching
        ? Math.max(
              keep -
                  excluded.filter((id) => brands.find((row) => row.storeBrandId === id)?.wouldKeep)
                      .length,
              0
          )
        : selectedPainted.filter((row) => !row.wouldAdd).length;
    const selectedCount = selectAllMatching
        ? Math.max(brandTotal - excluded.length, 0)
        : ticked.length;

    const toggleBrand = (id: string) => {
        if (locked) return;
        setReceipt(null);
        if (selectAllMatching) {
            setExcluded((prev) =>
                prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
            );
            return;
        }
        setTicked((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    const persist = async () => {
        if (!pickedId) return;
        setPhase('saving');
        setError('');
        try {
            const result = await applyTemplateToStoreBrands(
                fromBrands
                    ? {
                          templateId: pickedId,
                          storeBrandIds: namedTargets.map((row) => row.storeBrandId),
                      }
                    : selectAllMatching
                      ? {
                            templateId: pickedId,
                            selectAllMatching: true,
                            filters: {
                                search: debouncedBrand || undefined,
                                states: brandStates.length ? brandStates : undefined,
                            },
                            excludeStoreBrandIds: excluded,
                        }
                      : {
                            templateId: pickedId,
                            storeBrandIds: ticked,
                        }
            );
            setReceipt(result);
            setPhase('done');
            onApplied();
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('idle');
        }
    };

    const brandChips = [
        ...brandStates.map((state) => ({
            key: `st-${state}`,
            label: state,
            onClear: () => setBrandStates((prev) => prev.filter((item) => item !== state)),
        })),
        ...(debouncedBrand
            ? [
                  {
                      key: 'q',
                      label: `Search: ${debouncedBrand}`,
                      onClear: () => setBrandSearch(''),
                  },
              ]
            : []),
    ];
    const templateChips = [
        ...templateStates.map((state) => ({
            key: `st-${state}`,
            label: state,
            onClear: () => setTemplateStates((prev) => prev.filter((item) => item !== state)),
        })),
        ...(debouncedTemplate
            ? [
                  {
                      key: 'q',
                      label: `Search: ${debouncedTemplate}`,
                      onClear: () => setTemplateSearch(''),
                  },
              ]
            : []),
    ];

    const preview = fromBrands
        ? brandsPreview({
              pack,
              blockReasons,
              receipt,
              oneBrand,
              namedTargets,
              add: brandAddCount(brands, oneBrand),
              keep: brandKeepCount(brands, oneBrand),
              zero: brandAddCount(brands, oneBrand) === 0,
          })
        : templatesPreview({
              blocked: blockReasons.length > 0,
              receipt,
              selectedCount,
              selectedAdd,
              selectedKeep,
          });

    const confirmLabel = fromBrands
        ? brandAddCount(brands, oneBrand) === 0
            ? 'Apply this template'
            : oneBrand
              ? 'Apply to this store brand'
              : `Apply to ${plural(selectedAdd || namedTargets.length, 'store brand')}`
        : selectedAdd === 0
          ? 'Apply this template'
          : `Apply to ${plural(selectedAdd, 'store brand')}`;

    const confirmDisabled =
        locked ||
        phase === 'done' ||
        blockReasons.length > 0 ||
        brandsBusy ||
        templatesBusy ||
        (fromBrands
            ? !pack || brandAddCount(brands, oneBrand) === 0 || brandsState !== 'ready'
            : selectedAdd === 0 || packState !== 'ready');

    const title = fromBrands
        ? `Apply a template to ${
              oneBrand
                  ? namedTargets[0]?.name || 'this store brand'
                  : plural(namedTargets.length, 'selected store brand')
          }`
        : pack
          ? `Apply “${pack.name}” to store brands`
          : 'Apply this template to store brands';

    const subtitle = fromBrands
        ? `Applying a template turns its reports on for ${oneBrand ? 'this store brand' : 'these store brands'} — the template is not changed, nothing is stored on the store brand about which template was used, and adding a report to the template later does not reach ${oneBrand ? 'it' : 'them'}.`
        : `Turning on ${plural(legalReports.length, 'report')} for the store brands you pick, writing only what each one is missing. The template is not changed, and no store brand is linked to it afterwards.`;

    const scopeNote = fromBrands
        ? oneBrand
            ? `One store brand${namedTargets[0]?.state ? `, in ${namedTargets[0].state}` : ''}. Only reports available for that state can be turned on.`
            : `${plural(namedTargets.length, 'store brand')} across ${uniqueStates(namedTargets).join(', ') || 'mixed states'}. This runs on the set you selected — the rows never had to be painted one by one.`
        : undefined;

    return (
        <ReportsDialog
            open
            width={fromBrands ? 840 : 820}
            zIndex={fromBrands ? 360 : 340}
            onClose={locked ? () => undefined : onClose}
            closeOnBackdrop={!locked}
            closeOnEscape={!locked}
        >
            <ReportsDialogHeader
                kicker={fromBrands ? 'Apply a template from store brands' : 'Apply a template'}
                title={title}
                subtitle={
                    <>
                        {subtitle}
                        {scopeNote ? (
                            <div
                                style={{
                                    marginTop: 4,
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    color: 'var(--cpm-text-tertiary)',
                                }}
                            >
                                {scopeNote}
                            </div>
                        ) : null}
                    </>
                }
                onClose={locked ? undefined : onClose}
            />
            <ReportsDialogBody pad="picker">
                {fromBrands ? (
                    <BrandsSourceBody
                        locked={locked}
                        error={error}
                        receipt={receipt}
                        pack={pack}
                        templates={templates}
                        templateTotal={templateTotal}
                        templatesState={templatesState}
                        templatesBusy={templatesBusy}
                        chromeReady={templatesReadyOnce.current}
                        templateSearch={templateSearch}
                        onSearch={setTemplateSearch}
                        templateStates={templateStates}
                        onTemplateStates={setTemplateStates}
                        stateDocs={stateDocs}
                        statesFailed={statesFailed}
                        onRetryStates={loadStates}
                        chips={templateChips}
                        defaultNote={
                            openedTemplateStates.length &&
                            sameSet(templateStates, openedTemplateStates)
                                ? `Opened filtered to templates for ${openedTemplateStates.join(' and ')} plus all-states templates, so a ${openedTemplateStates[0]} store brand does not lead with another state’s templates. Clear or change it if ops needs the rest.`
                                : ''
                        }
                        onRetry={() => setReload((n) => n + 1)}
                        onLoadMore={() => void loadTemplatesPage(templatePage + 1)}
                        loadingMore={templateMoreBusy}
                        moreFailed={templateMoreFailed}
                        onPick={pickTemplate}
                        onRetryWrite={() => void persist()}
                        oneBrand={oneBrand}
                        brands={brands}
                        brandsState={brandsState}
                        brandsBusy={brandsBusy}
                    />
                ) : (
                    <TemplatesSourceBody
                        locked={locked}
                        error={error}
                        receipt={receipt}
                        pack={pack}
                        packState={packState}
                        legalCount={legalReports.length}
                        illegalCount={illegalReports.length}
                        blockReasons={blockReasons}
                        brandSearch={brandSearch}
                        onSearch={setBrandSearch}
                        brandStates={brandStates}
                        onBrandStates={setBrandStates}
                        stateDocs={stateDocs}
                        statesFailed={statesFailed}
                        onRetryStates={loadStates}
                        chips={brandChips}
                        defaultNote={brandDefaultNote(
                            pack,
                            brandStates,
                            pack && !pack.allStates ? pack.states : []
                        )}
                        brands={paintedRows}
                        brandTotal={brandTotal}
                        universeTotal={universeTotal || brandTotal}
                        add={add}
                        keep={keep}
                        legalOnTemplate={legalCount}
                        brandsState={brandsState}
                        brandsBusy={brandsBusy}
                        chromeReady={brandsReadyOnce.current}
                        painting={painting}
                        narrowed={narrowed}
                        showSkipped={showSkipped}
                        selectAllMatching={selectAllMatching}
                        selectedCount={selectedCount}
                        selectedIds={selectedIds}
                        onToggle={toggleBrand}
                        onSelectAll={() => {
                            setSelectAllMatching(true);
                            setExcluded([]);
                            setTicked([]);
                        }}
                        onClearSel={() => {
                            setSelectAllMatching(false);
                            setExcluded([]);
                            setTicked([]);
                        }}
                        onToggleSkipped={() => setShowSkipped((prev) => !prev)}
                        onBrowse={() => setBrowse(true)}
                        onRetryPack={() => setReload((n) => n + 1)}
                        onRetryBrands={() => setReload((n) => n + 1)}
                        onLoadMore={() => void loadBrandsPage(brandPage + 1)}
                        hasMore={brands.length < brandTotal}
                        loadingMore={brandMoreBusy}
                        moreFailed={brandMoreFailed}
                        onRetryWrite={() => void persist()}
                    />
                )}
            </ReportsDialogBody>
            <ReportsDialogFooter
                previewMain={preview.main}
                previewSub={preview.sub}
                saving={locked}
                savingLabel={
                    fromBrands
                        ? 'Turning this template’s reports on. The write has not finished — do not fire it again, and this dialog stays put until it does.'
                        : 'Turning these reports on for the store brands you picked. The write has not finished — do not fire it again, and this dialog stays put until it does.'
                }
                onCancel={onClose}
                primaryLabel={confirmLabel}
                onPrimary={() => void persist()}
                primaryDisabled={confirmDisabled}
            />
        </ReportsDialog>
    );
}

function BrandsSourceBody({
    locked,
    error,
    receipt,
    pack,
    templates,
    templateTotal,
    templatesState,
    templatesBusy,
    chromeReady,
    templateSearch,
    onSearch,
    templateStates,
    onTemplateStates,
    stateDocs,
    statesFailed,
    onRetryStates,
    chips,
    defaultNote,
    onRetry,
    onLoadMore,
    loadingMore,
    moreFailed,
    onPick,
    onRetryWrite,
    oneBrand,
    brands,
    brandsState,
    brandsBusy,
}: {
    locked: boolean;
    error: string;
    receipt: ApplyTemplateResult | null;
    pack: TemplateDetails | null;
    templates: TemplateListRow[];
    templateTotal: number;
    templatesState: PlaceLoadState;
    templatesBusy: boolean;
    chromeReady: boolean;
    templateSearch: string;
    onSearch: (next: string) => void;
    templateStates: string[];
    onTemplateStates: (next: string[]) => void;
    stateDocs: CpmState[];
    statesFailed: boolean;
    onRetryStates: () => void;
    chips: { key: string; label: string; onClear: () => void }[];
    defaultNote: string;
    onRetry: () => void;
    onLoadMore: () => void;
    loadingMore: boolean;
    moreFailed: boolean;
    onPick: (id: string) => void;
    onRetryWrite: () => void;
    oneBrand: boolean;
    brands: StoreBrandListRow[];
    brandsState: PlaceLoadState;
    brandsBusy: boolean;
}) {
    const blocks = packBlockReasons(pack);
    const legal = (pack?.reports ?? []).filter(canTurnOn);
    const addN = brandAddCount(brands, oneBrand);
    const keepN = brandKeepCount(brands, oneBrand);
    const skipped = brands.reduce((sum, row) => {
        const legalN = legal.length;
        return sum + Math.max(legalN - (row.wouldGain ?? 0) - (row.alreadyOnCount ?? 0), 0);
    }, 0);

    if (!chromeReady && templatesState === 'loading') {
        return (
            <div>
                <div style={{ paddingBottom: 16 }}>
                    <ReportsWait label="Getting the templates ops can apply here. Blank inside this dialog is not a system with no templates." />
                </div>
                <div className="flex" style={{ gap: 9, paddingBottom: 14 }}>
                    <div className="cpm-sk" style={{ height: 36, width: 300 }} />
                    <div className="cpm-sk" style={{ height: 36, width: 108 }} />
                </div>
                <ReportsSkeletonList rows={3} />
            </div>
        );
    }
    if (!chromeReady && templatesState === 'error') {
        return (
            <div>
                <ReportsAlert
                    tone="danger"
                    title="Couldn't load the templates"
                    body="The templates did not come back, so this says nothing about how many exist. Nothing has been applied, no store brand changed, and the store brand list behind this dialog is untouched."
                    reason={reportsAdminErrorReason(error)}
                    action={
                        <CpmButton variant="outline" size="sm" onClick={onRetry}>
                            Retry the templates
                        </CpmButton>
                    }
                />
            </div>
        );
    }
    if (templatesState === 'ready' && templateTotal === 0 && chips.length === 0) {
        return (
            <EmptyFrame
                title="No templates exist yet"
                body="There is no template to apply. Templates are made in Templates — and reports can still be turned on for this store brand one by one in the drill-in behind this dialog."
            />
        );
    }

    return (
        <div>
            {receipt ? (
                <div style={{ marginBottom: 16 }}>
                    <ReportsAlert
                        tone="success"
                        title={`Applied: add ${receipt.add}, keep ${receipt.keep}`}
                        body={`${plural(receipt.enablementsToWrite, 'report turn-on')} written. ${
                            receipt.keep
                                ? `${receipt.keep} already had every applicable report on and were skipped. `
                                : 'Nothing was skipped. '
                        }The template still holds what it held, and no store brand records that it was applied.`}
                    />
                </div>
            ) : null}
            {error && !receipt ? (
                <div style={{ marginBottom: 16 }}>
                    <ReportsAlert
                        tone="danger"
                        title="Couldn't apply this template"
                        body="Nothing was turned on and the template you picked is still picked. The template itself was never part of this write."
                        reason={reportsAdminErrorReason(
                            error,
                            'Nothing was turned on and the template you picked is still picked.'
                        )}
                        action={
                            <CpmButton variant="outline" size="sm" onClick={onRetryWrite}>
                                Retry the write
                            </CpmButton>
                        }
                    />
                </div>
            ) : null}

            <div className="flex flex-wrap items-center" style={{ gap: 9 }}>
                <SearchField
                    value={templateSearch}
                    onChange={onSearch}
                    placeholder={REPORTS_COPY.SEARCH_TEMPLATES_APPLY}
                    disabled={locked}
                    width={300}
                    size="sm"
                />
                <CpmFilterDropdown
                    label="State"
                    triggerVariant="chip"
                    portal
                    disabled={locked}
                    menuMinWidth={286}
                    failed={statesFailed}
                    failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                    failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                    onRetry={onRetryStates}
                    options={stateDocs
                        .map(stateName)
                        .filter(Boolean)
                        .map((item) => ({ value: item, label: item }))}
                    selected={templateStates}
                    onChange={onTemplateStates}
                />
            </div>
            <AppliedFilterChips
                chips={chips}
                onClearAll={() => {
                    onSearch('');
                    onTemplateStates([]);
                }}
            />
            {defaultNote ? (
                <div
                    style={{
                        marginTop: 10,
                        fontSize: 11.5,
                        fontWeight: 600,
                        lineHeight: 1.45,
                        color: 'var(--cpm-text-tertiary)',
                    }}
                >
                    {defaultNote}
                </div>
            ) : null}
            <div
                style={{
                    margin: '16px 0 10px',
                    fontSize: 12.5,
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {chips.length
                    ? `${templates.length} of ${templateTotal} templates match · showing ${templates.length}`
                    : `${templateTotal} templates on offer · showing ${templates.length}`}
            </div>

            {templatesBusy ? (
                <div>
                    <div style={{ paddingBottom: 12 }}>
                        <ReportsWait label="Matching the templates to that search and those filters…" />
                    </div>
                    <ReportsSkeletonList rows={3} />
                </div>
            ) : templatesState === 'error' ? (
                <ReportsAlert
                    tone="danger"
                    title="Couldn't load the templates"
                    body="The templates did not come back, so this says nothing about how many exist. Nothing has been applied, no store brand changed, and the store brand list behind this dialog is untouched."
                    reason={reportsAdminErrorReason(error)}
                    action={
                        <CpmButton variant="outline" size="sm" onClick={onRetry}>
                            Retry the templates
                        </CpmButton>
                    }
                />
            ) : templates.length === 0 ? (
                <FilteredEmpty
                    title="No template matches what you have on"
                    cause="The search and the state filter caused this, not a system with no templates."
                    restated={`Still on: ${chips.map((chip) => chip.label).join(' · ')}.`}
                    totalInPlace={`${templateTotal} active templates exist`}
                    onClear={() => {
                        onSearch('');
                        onTemplateStates([]);
                    }}
                />
            ) : (
                <div
                    className="overflow-auto"
                    style={{
                        maxHeight: 360,
                        border: '1px solid var(--cpm-border-default)',
                        borderRadius: 10,
                    }}
                >
                    {templates.map((row) => {
                        const picked = pack?._id === row._id;
                        return (
                            <button
                                key={row._id}
                                type="button"
                                disabled={locked}
                                onClick={() => onPick(row._id)}
                                className="grid w-full items-center text-left"
                                style={{
                                    gridTemplateColumns: '26px 1fr 170px 150px',
                                    gap: 12,
                                    padding: '11px 16px',
                                    border: 0,
                                    borderBottom: '1px solid var(--cpm-border-default)',
                                    background: picked ? 'var(--cpm-purple-tint-13)' : '#fff',
                                    cursor: 'pointer',
                                }}
                            >
                                <RadioDot on={picked} />
                                <div className="min-w-0">
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 800,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {row.name}
                                    </div>
                                    <div
                                        className="flex flex-wrap items-center"
                                        style={{ gap: 6, marginTop: 4 }}
                                    >
                                        {row.tags.length ? (
                                            row.tags.map((tag) => (
                                                <span
                                                    key={tag}
                                                    style={{
                                                        padding: '3px 8px',
                                                        borderRadius: 6,
                                                        background: 'var(--cpm-surface-tag)',
                                                        fontSize: 10.5,
                                                        fontWeight: 700,
                                                        color: 'var(--cpm-text-secondary)',
                                                    }}
                                                >
                                                    {tag}
                                                </span>
                                            ))
                                        ) : (
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: 'var(--cpm-text-tertiary)',
                                                }}
                                            >
                                                {REPORTS_COPY.NO_TAGS}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
                                    {row.allStates ? (
                                        <span
                                            style={{
                                                fontSize: 11.5,
                                                fontWeight: 600,
                                                color: 'var(--cpm-text-secondary)',
                                            }}
                                        >
                                            {REPORTS_COPY.ALL_STATES}
                                        </span>
                                    ) : (
                                        row.states.map((state) => (
                                            <CpmBadge key={state} tone="neutral">
                                                {state}
                                            </CpmBadge>
                                        ))
                                    )}
                                </div>
                                <div className="flex items-center justify-end" style={{ gap: 8 }}>
                                    <span
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 700,
                                            color: 'var(--cpm-text-secondary)',
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {row.reportCount === 0
                                            ? 'Holds 0 reports'
                                            : plural(row.reportCount, 'report')}
                                    </span>
                                    {picked ? (
                                        <CpmBadge tone="brand">
                                            {REPORTS_COPY.PICKED_TO_APPLY}
                                        </CpmBadge>
                                    ) : null}
                                </div>
                            </button>
                        );
                    })}
                    <InfiniteScrollSentinel
                        hasMore={templates.length < templateTotal}
                        loading={loadingMore}
                        error={moreFailed}
                        disabled={locked}
                        onLoadMore={onLoadMore}
                        showEnd={templates.length > 0}
                        endVariant="rule"
                    />
                </div>
            )}

            {pack ? (
                <div
                    style={{
                        marginTop: 16,
                        padding: '18px 20px',
                        border: '2px solid var(--cpm-brand-purple)',
                        borderRadius: 10,
                        background: 'var(--cpm-purple-tint-3)',
                    }}
                >
                    <div
                        className="uppercase"
                        style={{
                            fontSize: 9.5,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'var(--cpm-brand-purple)',
                        }}
                    >
                        Picked to apply
                    </div>
                    <div
                        style={{
                            marginTop: 5,
                            fontSize: 13.5,
                            fontWeight: 800,
                            letterSpacing: '-0.01em',
                        }}
                    >
                        {pack.name}
                    </div>
                    <div
                        style={{
                            marginTop: 4,
                            fontSize: 11.5,
                            fontWeight: 600,
                            lineHeight: 1.45,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        {plural(pack.reports.length, 'report')} in this template · {legal.length}{' '}
                        can legally be turned on
                        {skipped
                            ? ` · ${skipped} template-report/store brand pairs skipped because that report does not belong to the store brand’s state`
                            : ' · nothing skipped for state'}
                    </div>
                    {brandsState === 'loading' || brandsBusy ? (
                        <div style={{ marginTop: 12 }}>
                            <ReportsWait label="Counting what this template would add to the picked store brands." />
                        </div>
                    ) : (
                        <div className="flex flex-wrap" style={{ gap: 10, marginTop: 12 }}>
                            <DiffStat
                                value={addN}
                                label={oneBrand ? 'Reports to add' : 'Store brands that gain'}
                            />
                            <DiffStat
                                value={keepN}
                                label={oneBrand ? 'Already on, kept' : 'Already fully on, kept'}
                            />
                            <DiffStat value={skipped} label="Skipped for state" />
                        </div>
                    )}
                    {blocks.length ? (
                        <div className="flex flex-col" style={{ gap: 8, marginTop: 14 }}>
                            {blocks.map((reason) => (
                                <div
                                    key={reason}
                                    className="flex items-start"
                                    style={{
                                        gap: 10,
                                        padding: '12px 14px',
                                        border: '1.5px solid var(--cpm-shortfall-border)',
                                        borderRadius: 8,
                                        background: 'var(--cpm-shortfall-bg)',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            lineHeight: 1.5,
                                            color: 'var(--cpm-text-secondary)',
                                        }}
                                    >
                                        {reason}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function TemplatesSourceBody({
    locked,
    error,
    receipt,
    pack,
    packState,
    legalCount,
    illegalCount,
    blockReasons,
    brandSearch,
    onSearch,
    brandStates,
    onBrandStates,
    stateDocs,
    statesFailed,
    onRetryStates,
    chips,
    defaultNote,
    brands,
    brandTotal,
    universeTotal,
    add,
    keep,
    legalOnTemplate,
    brandsState,
    brandsBusy,
    chromeReady,
    painting,
    narrowed,
    showSkipped,
    selectAllMatching,
    selectedCount,
    selectedIds,
    onToggle,
    onSelectAll,
    onClearSel,
    onToggleSkipped,
    onBrowse,
    onRetryPack,
    onRetryBrands,
    onLoadMore,
    hasMore,
    loadingMore,
    moreFailed,
    onRetryWrite,
}: {
    locked: boolean;
    error: string;
    receipt: ApplyTemplateResult | null;
    pack: TemplateDetails | null;
    packState: PlaceLoadState;
    legalCount: number;
    illegalCount: number;
    blockReasons: string[];
    brandSearch: string;
    onSearch: (next: string) => void;
    brandStates: string[];
    onBrandStates: (next: string[]) => void;
    stateDocs: CpmState[];
    statesFailed: boolean;
    onRetryStates: () => void;
    chips: { key: string; label: string; onClear: () => void }[];
    defaultNote: string;
    brands: StoreBrandListRow[];
    brandTotal: number;
    universeTotal: number;
    add: number;
    keep: number;
    legalOnTemplate: number;
    brandsState: PlaceLoadState;
    brandsBusy: boolean;
    chromeReady: boolean;
    painting: boolean;
    narrowed: boolean;
    showSkipped: boolean;
    selectAllMatching: boolean;
    selectedCount: number;
    selectedIds: string[];
    onToggle: (id: string) => void;
    onSelectAll: () => void;
    onClearSel: () => void;
    onToggleSkipped: () => void;
    onBrowse: () => void;
    onRetryPack: () => void;
    onRetryBrands: () => void;
    onLoadMore: () => void;
    hasMore: boolean;
    loadingMore: boolean;
    moreFailed: boolean;
    onRetryWrite: () => void;
}) {
    if (!chromeReady && (packState === 'loading' || brandsState === 'loading')) {
        return (
            <div>
                <div style={{ paddingBottom: 16 }}>
                    <ReportsWait label="Counting the store brands this template could be applied to, and what each one is missing. Hundreds of store brands are not painted as rows." />
                </div>
                <div className="flex" style={{ gap: 9, paddingBottom: 14 }}>
                    <div className="cpm-sk" style={{ height: 36, width: 300 }} />
                    <div className="cpm-sk" style={{ height: 36, width: 108 }} />
                </div>
                <div className="cpm-sk" style={{ height: 96, width: '100%', marginBottom: 12 }} />
                <ReportsSkeletonList rows={3} />
            </div>
        );
    }
    if (!chromeReady && (packState === 'error' || brandsState === 'error')) {
        return (
            <div>
                <ReportsAlert
                    tone="danger"
                    title="Couldn't load the store brands"
                    body="The store brands this template could be applied to did not come back, so neither the rows nor the counts could be requested. This is not a system with no store brands, and nothing has been turned on. The template behind this dialog is unchanged and its membership is untouched."
                    reason={reportsAdminErrorReason(error)}
                    action={
                        <CpmButton
                            variant="outline"
                            size="sm"
                            onClick={packState === 'error' ? onRetryPack : onRetryBrands}
                        >
                            Retry the store brands
                        </CpmButton>
                    }
                />
            </div>
        );
    }
    if (blockReasons.length) {
        return <BlockedFrame reasons={blockReasons} />;
    }

    const trueEmpty = brandTotal === 0 && chips.length === 0;
    const filteredEmpty = brandTotal === 0 && chips.length > 0;
    const showSummary = !trueEmpty && brandTotal > 0 && (!painting || brands.length === 0);
    const listReady = painting && brands.length > 0;

    return (
        <div>
            {receipt ? (
                <div style={{ marginBottom: 16 }}>
                    <ReportsAlert
                        tone="success"
                        title={`Applied to ${plural(receipt.add, 'store brand')}`}
                        body={`Add ${receipt.add}, keep ${receipt.keep}. ${plural(receipt.enablementsToWrite, 'report turn-on')} written${
                            receipt.keep
                                ? `, and ${receipt.keep} selected already had all of these reports on and were skipped. `
                                : ', and nothing was skipped. '
                        }The template still holds ${plural(pack?.reports.length ?? 0, 'report')} and none of these store brands stores that this template was applied.`}
                    />
                </div>
            ) : null}
            {error && !receipt ? (
                <div style={{ marginBottom: 16 }}>
                    <ReportsAlert
                        tone="danger"
                        title="Couldn't apply this template"
                        body="Nothing was turned on for any store brand and your store brand selection is still here. The template itself was never part of this write — its reports are exactly what they were."
                        reason={reportsAdminErrorReason(
                            error,
                            'Nothing was turned on for any store brand and your store brand selection is still here.'
                        )}
                        action={
                            <CpmButton variant="outline" size="sm" onClick={onRetryWrite}>
                                Retry the write
                            </CpmButton>
                        }
                    />
                </div>
            ) : null}
            {legalCount > 0 && illegalCount > 0 ? (
                <div style={{ marginBottom: 16 }}>
                    <ReportsAlert
                        tone="warning"
                        title={`${plural(illegalCount, 'report')} in this template will not be turned on`}
                        body={`${
                            illegalCount === 1
                                ? 'It is Doesn’t run yet, with no builder linked, or Inactive in the catalogue, so apply skips it'
                                : 'They are Doesn’t run yet, with no builder linked, or Inactive in the catalogue, so apply skips them'
                        } and turns on the other ${legalCount}. Taking ${illegalCount === 1 ? 'it' : 'them'} out of the template is a membership edit in the checklist behind this dialog.`}
                    />
                </div>
            ) : null}

            <div className="flex flex-wrap items-center" style={{ gap: 9 }}>
                <SearchField
                    value={brandSearch}
                    onChange={onSearch}
                    placeholder={REPORTS_COPY.SEARCH_STORE_BRANDS}
                    disabled={locked}
                    width={300}
                    size="sm"
                />
                <CpmFilterDropdown
                    label="State"
                    triggerVariant="chip"
                    portal
                    disabled={locked}
                    menuMinWidth={286}
                    failed={statesFailed}
                    failedTitle={REPORTS_COPY.LOAD_FAIL_STATES}
                    failedBody={REPORTS_COPY.OPTIONS_FAIL_BODY}
                    onRetry={onRetryStates}
                    options={stateDocs
                        .map(stateName)
                        .filter(Boolean)
                        .map((item) => ({ value: item, label: item }))}
                    selected={brandStates}
                    onChange={onBrandStates}
                />
            </div>
            <AppliedFilterChips
                chips={chips}
                onClearAll={() => {
                    onSearch('');
                    onBrandStates([]);
                }}
            />
            {defaultNote ? (
                <div
                    style={{
                        marginTop: 10,
                        fontSize: 11.5,
                        fontWeight: 600,
                        lineHeight: 1.45,
                        color: 'var(--cpm-text-tertiary)',
                    }}
                >
                    {defaultNote}
                </div>
            ) : null}

            {brandsBusy ? (
                <div style={{ marginTop: 16 }}>
                    <div style={{ paddingBottom: 12 }}>
                        <ReportsWait label={REPORTS_COPY.LOADING_BRANDS} />
                    </div>
                    <ReportsSkeletonList rows={3} />
                </div>
            ) : brandsState === 'error' ? (
                <div style={{ marginTop: 16 }}>
                    <ReportsAlert
                        tone="danger"
                        title="Couldn't load the store brands"
                        body="The store brands this template could be applied to did not come back, so neither the rows nor the counts could be requested. This is not a system with no store brands, and nothing has been turned on. The template behind this dialog is unchanged and its membership is untouched."
                        reason={reportsAdminErrorReason(error)}
                        action={
                            <CpmButton variant="outline" size="sm" onClick={onRetryBrands}>
                                Retry the store brands
                            </CpmButton>
                        }
                    />
                </div>
            ) : (
                <>
                    <div
                        className="flex flex-wrap items-center justify-between"
                        style={{ gap: 14, margin: '16px 0 10px' }}
                    >
                        <div
                            style={{
                                fontSize: 12.5,
                                fontWeight: 800,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {selectedCount ? `${selectedCount} selected · ` : ''}
                            {brandTotal} of {universeTotal} store brands match · {add} would gain at
                            least one · {keep} already fully on
                        </div>
                        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                            {keep > 0 && !trueEmpty ? (
                                <CpmButton variant="ghost" size="sm" onClick={onToggleSkipped}>
                                    {showSkipped
                                        ? 'Hide the store brands already fully on'
                                        : 'Show the store brands already fully on'}
                                </CpmButton>
                            ) : null}
                            <CpmButton
                                variant="outline"
                                size="sm"
                                disabled={brandTotal === 0 || locked}
                                onClick={onSelectAll}
                            >
                                Select all matching filters
                            </CpmButton>
                            <CpmButton
                                variant="ghost"
                                size="sm"
                                disabled={
                                    (!selectAllMatching && selectedIds.length === 0) || locked
                                }
                                onClick={onClearSel}
                            >
                                Clear selection
                            </CpmButton>
                        </div>
                    </div>

                    {showSummary ? (
                        <div
                            style={{
                                padding: '18px 20px',
                                border: '1px solid var(--cpm-border-default)',
                                borderRadius: 10,
                                background: 'var(--cpm-surface-subtle)',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 13.5,
                                    fontWeight: 800,
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                {narrowed
                                    ? 'What the filters you have on match'
                                    : 'Every store brand, counted'}
                            </div>
                            <div className="flex flex-wrap" style={{ gap: 10, marginTop: 12 }}>
                                <DiffStat value={brandTotal} label="Store brands match" />
                                <DiffStat value={add} label="Would gain at least one" />
                                <DiffStat value={keep} label="Already fully on" />
                            </div>
                            <div
                                style={{
                                    marginTop: 12,
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    lineHeight: 1.45,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                {painting && brands.length === 0
                                    ? 'Every store brand in this match already has all of these reports on, so there is no row to paint and nothing to write. '
                                    : `There are ${universeTotal} store brands, so this picker counts them and paints none until the set is narrowed — search a name, pick a state, or browse a short page. `}
                                Select all matching filters takes the whole set above without
                                ticking a row, and confirm can run on store brands that were never
                                painted.
                            </div>
                            {!painting && add > 0 ? (
                                <div style={{ marginTop: 14 }}>
                                    <CpmButton variant="outline" size="sm" onClick={onBrowse}>
                                        Browse the first 25 that would change
                                    </CpmButton>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {trueEmpty ? (
                        <EmptyFrame
                            title="There are no store brands in the system"
                            body="Not a search with no hits — there is nothing to apply a template to yet. The template keeps its reports and can be applied once a store brand is onboarded."
                        />
                    ) : null}
                    {filteredEmpty ? (
                        <FilteredEmpty
                            title="No store brand matches what you have on"
                            cause="The search and filters caused this, not an empty system."
                            restated={`Still on: ${chips.map((chip) => chip.label).join(' · ')}.`}
                            totalInPlace={`${universeTotal} store brands exist`}
                            onClear={() => {
                                onSearch('');
                                onBrandStates([]);
                            }}
                        />
                    ) : null}

                    {listReady ? (
                        <div
                            className="overflow-auto"
                            style={{
                                maxHeight: 360,
                                border: '1px solid var(--cpm-border-default)',
                                borderRadius: 10,
                            }}
                        >
                            <div
                                className="grid items-center sticky top-0 z-[2]"
                                style={{
                                    gridTemplateColumns: '30px 1fr 190px',
                                    gap: 12,
                                    padding: '9px 16px',
                                    background: '#EDEDF3',
                                    borderBottom: '1.5px solid var(--cpm-border-strong)',
                                }}
                            >
                                <span />
                                <span
                                    className="uppercase"
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: '0.07em',
                                        color: 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    Store brand
                                </span>
                                <span
                                    className="uppercase"
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: '0.07em',
                                        color: 'var(--cpm-text-secondary)',
                                        textAlign: 'right',
                                    }}
                                >
                                    This template
                                </span>
                            </div>
                            {brands.map((row) => {
                                const gain = row.wouldGain ?? (row.wouldAdd ? 1 : 0);
                                const already = row.alreadyOnCount ?? 0;
                                const legalN = legalOnTemplate || already + gain;
                                return (
                                    <div
                                        key={row.storeBrandId}
                                        className="grid items-center"
                                        onClick={() => onToggle(row.storeBrandId)}
                                        style={{
                                            gridTemplateColumns: '30px 1fr 190px',
                                            gap: 12,
                                            padding: '11px 16px',
                                            borderTop: '1px solid var(--cpm-border-default)',
                                            cursor: 'pointer',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background =
                                                'var(--cpm-purple-tint-3)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        <span
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex"
                                        >
                                            <CpmCheckbox
                                                checked={selectedIds.includes(row.storeBrandId)}
                                                onChange={() => onToggle(row.storeBrandId)}
                                            />
                                        </span>
                                        <div className="min-w-0">
                                            <div
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.01em',
                                                }}
                                            >
                                                {row.name}
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 3,
                                                    fontSize: 11.5,
                                                    fontWeight: 500,
                                                    color: 'var(--cpm-text-secondary)',
                                                }}
                                            >
                                                {row.state ?? (row.mixedStates?.join(', ') || '—')}{' '}
                                                · {already} of {legalN} of this template already on
                                            </div>
                                        </div>
                                        <div className="flex justify-end">
                                            <CpmBadge tone={gain > 0 ? 'brand' : 'success'}>
                                                {gain > 0
                                                    ? `Would gain ${gain}`
                                                    : 'Already fully on'}
                                            </CpmBadge>
                                        </div>
                                    </div>
                                );
                            })}
                            <InfiniteScrollSentinel
                                hasMore={hasMore}
                                loading={loadingMore}
                                error={moreFailed}
                                disabled={locked}
                                onLoadMore={onLoadMore}
                                showEnd={brands.length > 0}
                                endVariant="rule"
                            />
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}

function packBlockReasons(pack: TemplateDetails | null): string[] {
    if (!pack) return [];
    const legal = pack.reports.filter(canTurnOn);
    const blocks: string[] = [];
    if (pack.activeStatus === 'Inactive') {
        blocks.push(
            'This template is Inactive. Inactive hides a template from new applies — it stays in the Templates list, and its details and membership can still be edited.'
        );
    }
    if (pack.reports.length === 0) {
        blocks.push(
            'This template holds 0 reports, so there is nothing to turn on. Zero here is a loaded number, not a failed request.'
        );
    } else if (legal.length === 0) {
        blocks.push(
            `None of the ${plural(pack.reports.length, 'report')} in this template can be turned on — each is Doesn’t run yet, has no builder linked, or is Inactive in the catalogue.`
        );
    }
    return blocks;
}

function brandAddCount(brands: StoreBrandListRow[], oneBrand: boolean): number {
    if (oneBrand) return brands[0]?.wouldGain ?? 0;
    return brands.filter((row) => row.wouldAdd).length;
}

function brandKeepCount(brands: StoreBrandListRow[], oneBrand: boolean): number {
    if (oneBrand) return brands[0]?.alreadyOnCount ?? 0;
    return brands.filter((row) => !row.wouldAdd).length;
}

function templatesPreview({
    blocked,
    receipt,
    selectedCount,
    selectedAdd,
    selectedKeep,
}: {
    blocked: boolean;
    receipt: ApplyTemplateResult | null;
    selectedCount: number;
    selectedAdd: number;
    selectedKeep: number;
}): { main: string; sub: string } {
    if (blocked) {
        return {
            main: 'This apply is blocked',
            sub: 'Nothing can be confirmed while the reason above stands. The template and every store brand are untouched.',
        };
    }
    if (receipt) {
        return {
            main: `Done. Add ${receipt.add}, keep ${receipt.keep}.`,
            sub: 'The template was not changed, and no store brand is linked to it.',
        };
    }
    if (selectedAdd === 0) {
        return {
            main: 'Nothing would change',
            sub:
                selectedCount === 0
                    ? 'Nothing is selected yet, so there is no write to make. Confirm stays off until at least one store brand would gain a report.'
                    : `All ${selectedCount} selected store brands already have every report in this template on, so this would write nothing. Confirm stays off rather than firing a no-op.`,
        };
    }
    return {
        main: `Add ${selectedAdd} · keep ${selectedKeep}`,
        sub: `${plural(selectedAdd, 'store brand')} would gain at least one report${
            selectedKeep
                ? `, and ${selectedKeep} selected are already fully on and will be skipped`
                : ', and nothing is skipped'
        }. Only the missing turn-ons are written; the template is not changed.`,
    };
}

function brandsPreview({
    pack,
    blockReasons,
    receipt,
    oneBrand,
    namedTargets,
    add,
    keep,
    zero,
}: {
    pack: TemplateDetails | null;
    blockReasons: string[];
    receipt: ApplyTemplateResult | null;
    oneBrand: boolean;
    namedTargets: PresetStoreBrand[];
    add: number;
    keep: number;
    zero: boolean;
}): { main: string; sub: string } {
    if (!pack) {
        return {
            main: 'Pick a template',
            sub: 'Nothing is picked yet, so there is nothing to preview. Templates are dozens — search or filter, then pick one.',
        };
    }
    if (blockReasons.length) {
        return {
            main: 'This apply is blocked',
            sub: 'Nothing can be confirmed while the reason above stands. Every store brand is untouched.',
        };
    }
    if (receipt) {
        return {
            main: `Done. Add ${receipt.add}, keep ${receipt.keep}.`,
            sub: 'The template was not changed, and no store brand is linked to it.',
        };
    }
    if (zero) {
        return {
            main: 'Nothing would change',
            sub: oneBrand
                ? `This store brand already has every report in “${pack.name}” that applies${namedTargets[0]?.state ? ` in ${namedTargets[0].state}` : ''}. Confirm stays off rather than firing a no-op.`
                : `All ${namedTargets.length} selected store brands already have every applicable report in “${pack.name}” on, so this would write nothing. Confirm stays off.`,
        };
    }
    return {
        main: oneBrand
            ? `Add ${plural(add, 'report')} · keep ${keep}`
            : `Add ${plural(add, 'store brand')} · keep ${keep}`,
        sub: oneBrand
            ? `${plural(add, 'report')} would be turned on for ${namedTargets[0]?.name || 'this store brand'}, and ${keep} — the reports it already has — are kept. Only missing turn-ons are written; the template is not changed.`
            : `${plural(add, 'store brand')} would gain at least one report${
                  keep
                      ? `, and ${keep} are already fully on and will be skipped`
                      : ', and nothing is skipped'
              }. Only missing turn-ons are written; the template is not changed.`,
    };
}

function brandDefaultNote(
    pack: TemplateDetails | null,
    brandStates: string[],
    defaults: string[]
): string {
    if (!pack) return '';
    if (defaults.length && sameSet(brandStates, defaults)) {
        return `This template is tagged for ${defaults.join(' and ')}, so the picker opened filtered to store brands in ${defaults.length === 1 ? 'that state' : 'those states'}. Clear or change it if ops needs the rest.`;
    }
    if (pack.allStates && brandStates.length === 0) {
        return 'This template is all-states, so the picker opened with no state filter — there is no single state to fake. Search a name or pick states yourself.';
    }
    return '';
}

function uniqueStates(rows: PresetStoreBrand[]): string[] {
    return Array.from(
        new Set(rows.map((row) => row.state).filter((state): state is string => Boolean(state)))
    );
}

function defaultOpenedTemplateStates(rows: PresetStoreBrand[]): string[] {
    if (rows.length === 0) return [];
    const first = rows[0]?.state;
    if (!first) return [];
    if (rows.every((row) => row.state === first)) return [first];
    return [];
}

function sameSet(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const other = new Set(b);
    return a.every((item) => other.has(item));
}

function RadioDot({ on }: { on: boolean }) {
    return (
        <span
            style={{
                display: 'block',
                width: 16,
                height: 16,
                borderRadius: 999,
                border: on
                    ? '5px solid var(--cpm-brand-purple)'
                    : '1.5px solid var(--cpm-border-strong)',
                background: '#fff',
                boxShadow: on ? '0 0 0 1.5px var(--cpm-brand-purple)' : 'none',
            }}
        />
    );
}

function DiffStat({ value, label }: { value: number; label: string }) {
    return (
        <div
            style={{
                flex: '1 1 120px',
                padding: '12px 14px',
                background: '#fff',
                border: '1px solid var(--cpm-border-default)',
                borderRadius: 8,
            }}
        >
            <div
                style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {value}
            </div>
            <div
                className="uppercase"
                style={{
                    marginTop: 3,
                    fontSize: 9.5,
                    fontWeight: 800,
                    letterSpacing: '0.07em',
                    color: 'var(--cpm-text-tertiary)',
                }}
            >
                {label}
            </div>
        </div>
    );
}

function EmptyFrame({ title, body }: { title: string; body: string }) {
    return (
        <div
            className="flex flex-col items-center text-center"
            style={{
                padding: '26px 22px',
                border: '1px solid var(--cpm-border-default)',
                borderRadius: 10,
            }}
        >
            <div
                className="flex items-center justify-center"
                style={{
                    width: 72,
                    height: 72,
                    borderRadius: 18,
                    background: 'var(--cpm-open-bg)',
                    color: 'var(--cpm-open-icon)',
                    marginBottom: 16,
                }}
            >
                <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M3 9h18M5 9V7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v2M5 9v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9" />
                </svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>{title}</div>
            <div
                style={{
                    maxWidth: 440,
                    marginTop: 7,
                    fontSize: 13,
                    fontWeight: 500,
                    lineHeight: 1.5,
                    color: 'var(--cpm-text-secondary)',
                }}
            >
                {body}
            </div>
        </div>
    );
}

function BlockedFrame({ reasons }: { reasons: string[] }) {
    return (
        <div className="flex flex-col items-center text-center" style={{ padding: '22px 4px 8px' }}>
            <div
                className="flex items-center justify-center"
                style={{
                    width: 72,
                    height: 72,
                    borderRadius: 18,
                    background: 'var(--cpm-shortfall-bg)',
                    color: 'var(--cpm-shortfall-icon)',
                    marginBottom: 18,
                }}
            >
                <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <rect x="5" y="11" width="14" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em' }}>
                This template cannot be applied
            </div>
            <div className="flex flex-col" style={{ gap: 8, marginTop: 8, maxWidth: 480 }}>
                {reasons.map((reason) => (
                    <div
                        key={reason}
                        style={{
                            fontSize: 13,
                            fontWeight: 500,
                            lineHeight: 1.5,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        {reason}
                    </div>
                ))}
            </div>
            <div
                style={{
                    marginTop: 20,
                    paddingTop: 16,
                    borderTop: '1px solid var(--cpm-border-default)',
                    maxWidth: 480,
                    width: '100%',
                    fontSize: 11.5,
                    fontWeight: 600,
                    lineHeight: 1.45,
                    color: 'var(--cpm-text-tertiary)',
                }}
            >
                Editing the template is still allowed — the checklist behind this dialog adds and
                removes reports, and the details form sets it back to Active. Nothing has been
                turned on for any store brand.
            </div>
        </div>
    );
}
